import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { CarrierDeliveryNoteBundleOverview } from '../../../api/models/carrier-delivery-note-bundle-overview';
import { CarrierDeliveryNoteBundle } from '../../../api/models/carrier-delivery-note-bundle';
import { DeliveryNoteBundlesService } from '../../../api/services/delivery-note-bundles.service';
import { DeliveryNotesService } from '../../../api/services/delivery-notes.service';
import { CarrierDeliveryNoteInPool } from '../../../api/models/carrier-delivery-note-in-pool';
import { TourResponseV3 } from '../../../api/addons-v3/models/tour-response-v-3';
import { TourPlanStore } from '@app/stores/tourPlanStore';
import { DeliveryNoteStatus } from '../../../api/models/delivery-note-status';

@Injectable({
    providedIn: 'root',
})
export class TourPlanService {
    private tourPlanStore = inject(TourPlanStore);

    constructor(
        private deliveryNoteBundleService: DeliveryNoteBundlesService,
        private deliveryNotesService: DeliveryNotesService
    ) {}

    /**
     * setting the plan mode (either active or inactive). If the active mode is set, the delivery notes pool is loaded
     * @param tourPlanMode
     * @param orgSiteKey
     * @param tour
     */
    public async setTourPlanMode(
        tourPlanMode: boolean,
        orgSiteKey?: string,
        tour?: TourResponseV3
    ) {
        if (tourPlanMode) {
            //fetch the delivery notes pool if planning mode has been activated
            const response = await lastValueFrom(
                this.deliveryNotesService.getCarrierDeliveryNotesMetadataInPool(
                    {
                        organizationSiteKey: orgSiteKey!,
                    }
                )
            );
            this.tourPlanStore.setDeliveryNotesPool(response);

            if (tour) {
                //edit mode has been activated
                this.prepareForEdit(tour);
            }
        } else {
            this.tourPlanStore.setDeliveryNotesPool([]);
        }
    }

    public addTourPlanDeliveryNotes(
        notes: CarrierDeliveryNoteInPool[]
    ): boolean {
        return this.tourPlanStore.addTourPlanDeliveryNotes(notes);
    }

    /**
     * remove delivery notes from the tour plan
     * @param notes
     */
    public removeTourPlanDeliveryNotes(notes: CarrierDeliveryNoteInPool[]) {
        this.tourPlanStore.removeTourPlanDeliveryNotes(notes);
    }

    /**
     * add bundles (and all delivery notes from these bundles) to the tour plan
     * @param bundleKeys
     * @param orgSiteKey
     */
    public async addTourPlanBundles(
        bundleKeys: string[],
        orgSiteKey: string
    ): Promise<boolean> {
        // Filter out bundles that are already added to tour plan
        const existingBundleKeys = new Set(
            this.tourPlanStore.tourPlanBundles().map(bundle => bundle._key)
        );
        const newBundleKeys = bundleKeys.filter(
            key => !existingBundleKeys.has(key)
        );

        if (newBundleKeys.length === 0) {
            return false;
        }

        //download the bundle details to retrieve all delivery notes
        let promises: Promise<CarrierDeliveryNoteBundle>[] = [];
        for (const bundleKey of newBundleKeys) {
            promises.push(
                lastValueFrom(
                    this.deliveryNoteBundleService.getCarrierBundle({
                        deliveryNoteBundleKey: bundleKey,
                        organizationSiteKey: orgSiteKey,
                    })
                )
            );
        }
        let deliveryNotes: CarrierDeliveryNoteInPool[] = [];
        let carrierDeliveryNoteBundles: CarrierDeliveryNoteBundle[] = [];
        const pool = this.tourPlanStore.deliveryNotesPool();
        await Promise.all(promises).then(results => {
            results.forEach(result => {
                result.deliveryNotes.forEach(bundleDeliveryNote => {
                    const foundNote = pool.find(
                        note => note._key === bundleDeliveryNote._key
                    );
                    if (foundNote) {
                        deliveryNotes.push(foundNote);
                    } else {
                        console.warn(
                            'Delivery note not found in pool:',
                            bundleDeliveryNote._key
                        );
                        // Transform bundle delivery note to CarrierDeliveryNoteInPool format
                        const transformedNote: CarrierDeliveryNoteInPool = {
                            _key: bundleDeliveryNote._key,
                            attachmentsCount:
                                bundleDeliveryNote.attachmentsCount || 0,
                            carrierKey: result.carrierKey || '',
                            carrierName: result.carrierName || '',
                            carrierNumberPlateIncoming:
                                bundleDeliveryNote.carrierNumberPlateIncoming ||
                                '',
                            carrierNumberPlateOutgoing:
                                result.carrierNumberPlateOutgoing || '',
                            checkinKey: '',
                            consigneeKey: result.consigneeKey || '',
                            consigneeLoadCarrierExchange:
                                bundleDeliveryNote.consigneeLoadCarrierExchange ||
                                false,
                            consigneeLoadCarrierImages:
                                bundleDeliveryNote.consigneeLoadCarrierImages ||
                                false,
                            consigneeName: result.consigneeName || '',
                            consignorKey: result.consignorKey || '',
                            consignorLoadCarrierExchange:
                                bundleDeliveryNote.consignorLoadCarrierExchange ||
                                false,
                            consignorLoadCarrierImages:
                                bundleDeliveryNote.consignorLoadCarrierImages ||
                                false,
                            consignorName: result.consignorName || '',
                            dateAnnounced:
                                bundleDeliveryNote.dateAnnounced || 0,
                            showDateAnnouncedTime:
                                bundleDeliveryNote.showDateAnnouncedTime ||
                                false,
                            dateCreated: bundleDeliveryNote.dateCreated || 0,
                            dateReceived: bundleDeliveryNote.dateReceived || 0,
                            dateShipped: result.dateShipped || 0,
                            deliveryNoteBundleKey: result._key,
                            deliveryNoteBundleName: result.name || '',
                            deliveryNoteBundleNumber: result.bundleNumber || '',
                            deliveryNoteBundleStatus: result.status as
                                | 'sent'
                                | 'closed',
                            discrepancies:
                                bundleDeliveryNote.discrepancies || false,
                            externalTourName: '',
                            fileName: bundleDeliveryNote.fileName || '',
                            fineDiscrepancies:
                                bundleDeliveryNote.fineDiscrepancies || false,
                            loadCarriers: bundleDeliveryNote.loadCarriers || [],
                            number: bundleDeliveryNote.number || '',
                            orderNumber: bundleDeliveryNote.orderNumber,
                            originalConsignor:
                                bundleDeliveryNote.originalConsignor,
                            originalConsignorGln:
                                bundleDeliveryNote.originalConsignorGln,
                            palletTickets:
                                bundleDeliveryNote.palletTickets || false,
                            partOfTour: false,
                            properties: bundleDeliveryNote.properties,
                            tourKey: '',
                            tourNumber: '',
                            status: result.status as DeliveryNoteStatus,
                        };
                        deliveryNotes.push(transformedNote);
                    }
                });
                carrierDeliveryNoteBundles.push(result);
            });
        });
        //add the delivery notes to the tour plan
        this.tourPlanStore.addTourPlanDeliveryNotes(deliveryNotes);

        // Create bundle overviews from the fetched bundle data
        const bundleOverviews: CarrierDeliveryNoteBundleOverview[] =
            carrierDeliveryNoteBundles.map(bundle => ({
                _key: bundle._key,
                bundleNumber: bundle.bundleNumber,
                numberOfDeliveryNotes: bundle.deliveryNotes?.length || 0,
            })) as CarrierDeliveryNoteBundleOverview[];

        //add bundles and overviews to the store
        this.tourPlanStore.addTourPlanBundles(
            carrierDeliveryNoteBundles,
            bundleOverviews
        );

        return true;
    }

    /**
     * prepare for edit mode
     * @param tour
     * @private
     */
    private prepareForEdit(tour: TourResponseV3) {
        let notesToAdd: CarrierDeliveryNoteInPool[] = [];
        const pool = this.tourPlanStore.deliveryNotesPool();

        for (const key of tour.deliveryNoteKeys) {
            const noteFound = pool.find(note => note._key === key);
            if (noteFound) {
                notesToAdd.push(noteFound);
            }
        }

        this.tourPlanStore.setEditMode(tour._key, tour.externalTourName);
        this.tourPlanStore.addTourPlanDeliveryNotes(notesToAdd);
    }

    /**
     * clears the planning
     */
    public clear(): void {
        this.tourPlanStore.clearTourPlan();
    }
}
